import { prepareCanonicalFeedback } from './canonicalFeedback';
import { CanonicalActionUnavailableError } from './canonicalActionError';
import { evaluateFlagGuards } from '@apruv/survey-contract/flags';
import { canonicalPreviewContext } from '../preview/canonicalPreviewContext';
import {
  enterSurvey,
  nextSurveyDestination,
  type CompiledSurvey,
  type SurveyDestination,
} from '@apruv/survey-contract/compiler';
import type { CXPublicSurveyResponse } from '../../services/cxPublicSurveyService';
import type { FlowActionEvent, FlowValues } from '../types';
import {
  canonicalAnswerContext,
  canonicalExecutionPolicy,
  canonicalSurveyContext,
} from './canonicalSurveyContext';
import {
  assertCanonicalComponentIds,
  RESERVED_SURVEY_VALUE_KEYS,
  canonicalComponentConfig,
  presentationComponentDestination,
  visiblePresentationComponents,
} from './surveyPresentation';

export interface CanonicalInteractionResult {
  destination: SurveyDestination;
  values: FlowValues;
  ratingComponentId?: string;
  action?: FlowActionEvent;
  editResponse?: boolean;
  guardDenied?: boolean;
}
export interface CanonicalFlowDriver {
  entry: SurveyDestination;
  revisit?: { message: boolean; tipComponentId?: string };
  acknowledgeResponse?: () => void;
  usesHistoryBack: (screenId: string, componentId: string) => boolean;
  initialRatingComponentId?: string;
  initialAcknowledgedAnswers?: FlowValues;
  summaryEdit: (
    screenId: string,
    values: FlowValues,
    ratingComponentId?: string
  ) => CanonicalInteractionResult | undefined;
  prepareFeedback?: (
    screenId: string,
    values: FlowValues,
    ratingComponentId: string | undefined,
    skip: boolean,
    acknowledgedAnswers?: FlowValues
  ) => FlowValues;
  verifyFeedbackAcknowledgement?: (
    screenId: string,
    values: FlowValues,
    ratingComponentId: string | undefined,
    savedIds: readonly string[],
    priorAnswers: FlowValues
  ) => void;
  validateValue: (
    screenId: string,
    componentId: string,
    values: FlowValues,
    ratingComponentId?: string,
    update?: { value: unknown; submission?: boolean }
  ) => string | undefined;
  guard: (
    screenId: string,
    values: FlowValues,
    ratingComponentId?: string,
    componentId?: string,
    action?: FlowActionEvent
  ) => CanonicalInteractionResult | undefined;
  resolve: (
    screenId: string,
    values: FlowValues,
    ratingComponentId?: string,
    componentId?: string,
    action?: FlowActionEvent
  ) => CanonicalInteractionResult;
}

export function createCanonicalFlowDriver(
  compiled: CompiledSurvey,
  response: CXPublicSurveyResponse
): CanonicalFlowDriver {
  assertCanonicalComponentIds(compiled);
  const base = canonicalSurveyContext(compiled, response);
  let acknowledgedResponse = false;
  const policy = canonicalExecutionPolicy(response);
  const revisitPolicy = compiled.snapshot.configuration.revisitPolicy;
  const restrictedRevisit =
    base.has_existing_response === true && revisitPolicy.behavior !== 'allow_resubmit';
  const tipCandidates =
    restrictedRevisit &&
    base.tipping_enabled &&
    (revisitPolicy.behavior === 'tip_only' || revisitPolicy.showTipButton)
      ? compiled.snapshot.graph.nodes.flatMap((node) => {
          if (
            !node.screenId ||
            !evaluateFlagGuards(compiled.snapshot, 'stage', node.id, base.resolvedFlags).allowed
          )
            return [];
          return visiblePresentationComponents(compiled, node.screenId, base)
            .filter((component) => {
              if (component.type !== 'tip_section') return false;
              const tips = compiled.snapshot.actions.filter(
                (action) => component.actionIds.includes(action.id) && action.type === 'tip'
              );
              return (
                tips.length === 1 &&
                evaluateFlagGuards(compiled.snapshot, 'action', tips[0].id, base.resolvedFlags)
                  .allowed
              );
            })
            .map((component) => ({ node, component }));
        })
      : [];
  // An ambiguous payment owner must never be selected implicitly.
  const revisitTip = tipCandidates.length === 1 ? tipCandidates[0] : undefined;
  const assertRevisitInteraction = (componentId?: string) => {
    if (restrictedRevisit && (!revisitTip || componentId !== revisitTip.component.id))
      throw new Error('This return visit only supports the available tipping step.');
  };
  const ratingTypes = new Set(['rating_scale', `${base.score_method}_rating`]);
  const ratings = compiled.snapshot.screens
    .flatMap((screen) => screen.components)
    .filter((component) => ratingTypes.has(component.type));
  const answerComponents = compiled.snapshot.screens
    .flatMap((screen) => screen.components)
    .filter(
      (component) =>
        ratingTypes.has(component.type) || ['comment_box', 'tag_selector'].includes(component.type)
    );
  const componentIds = new Set(
    compiled.snapshot.screens.flatMap((screen) =>
      screen.components.map((component) => component.id)
    )
  );
  const protectedKeys = new Set([
    ...RESERVED_SURVEY_VALUE_KEYS,
    ...answerComponents.map((component) => component.id),
  ]);
  if (response.existingResponse && ratings.length !== 1)
    throw new Error('The saved rating needs an unambiguous component owner.');
  const inspectAction = (
    screenId: string,
    currentValues: FlowValues,
    previousRatingId: string | undefined,
    componentId: string,
    action?: FlowActionEvent
  ) => {
    assertRevisitInteraction(componentId);
    if (action && action.componentId !== componentId)
      throw new Error('The action does not belong to this component.');
    const component = compiled.snapshot.screens
      .find((screen) => screen.id === screenId)
      ?.components.find((item) => item.id === componentId);
    if (!component) throw new Error('The selected survey component no longer exists.');
    const suppliedActionId = action?.metadata?.actionId;
    const paymentWidget = ['tip_section', 'finix_tip_section'].includes(component.type);
    const paymentSkip = paymentWidget && action?.type === 'continue' && action.value === false;
    if (restrictedRevisit && action?.type !== 'tip' && !paymentSkip)
      throw new Error('Only tipping or skipping is allowed on this return visit.');
    const choices =
      paymentWidget && (paymentSkip || action?.type === 'tip')
        ? component.actionIds.filter(
            (id) =>
              compiled.snapshot.actions.find((item) => item.id === id)?.type ===
              (paymentSkip ? 'continue' : 'tip')
          )
        : component.actionIds;
    const actionId =
      typeof suppliedActionId === 'string'
        ? suppliedActionId
        : choices.length === 1
          ? choices[0]
          : undefined;
    if (choices.length > 1 && !actionId)
      throw new Error('This component needs an explicit action selection.');
    if (actionId && !component.actionIds.includes(actionId))
      throw new Error('The selected action is not owned by this component.');
    const guardContext = canonicalAnswerContext(base, currentValues, previousRatingId, compiled);
    const configuredAction = actionId
      ? compiled.snapshot.actions.find((item) => item.id === actionId)
      : undefined;
    if (
      configuredAction &&
      action &&
      configuredAction.type !== action.type &&
      !(configuredAction.type === 'submit' && action.type === 'continue')
    )
      throw new Error('The interaction does not match its configured action.');
    if (
      configuredAction &&
      !evaluateFlagGuards(
        compiled.snapshot,
        'action',
        configuredAction.id,
        guardContext.resolvedFlags
      ).allowed
    ) {
      const guardDestination: SurveyDestination = restrictedRevisit
        ? { kind: 'terminal' }
        : presentationComponentDestination(
            compiled,
            screenId,
            componentId,
            guardContext,
            actionId ? { actionId } : undefined
          );
      return {
        component,
        actionId,
        denial: {
          destination: guardDestination,
          values: { ...currentValues },
          ratingComponentId: previousRatingId,
          guardDenied: true as const,
        },
      };
    }
    return { component, actionId, paymentSkip, denial: undefined };
  };
  let entry: SurveyDestination = restrictedRevisit
    ? revisitTip
      ? { kind: 'screen', screenId: revisitTip.node.screenId!, nodeId: revisitTip.node.id }
      : { kind: 'terminal' }
    : enterSurvey(compiled, base);
  const previewScreen = canonicalPreviewContext(response)?.initialScreenId;
  if (previewScreen) {
    const node = compiled.snapshot.graph.nodes.find((item) => item.screenId === previewScreen);
    if (
      !node ||
      !evaluateFlagGuards(compiled.snapshot, 'stage', node.id, base.resolvedFlags).allowed
    )
      throw new Error('This screen is unavailable in the current preview scenario.');
    if (!restrictedRevisit) entry = { kind: 'screen', screenId: previewScreen, nodeId: node.id };
  }
  return {
    entry,
    ...(restrictedRevisit
      ? {
          revisit: {
            message: revisitPolicy.behavior === 'show_message',
            tipComponentId: revisitTip?.component.id,
          },
        }
      : {}),
    acknowledgeResponse() {
      acknowledgedResponse = true;
    },
    guard(screenId, values, ratingComponentId, componentId, action) {
      return componentId
        ? inspectAction(screenId, values, ratingComponentId, componentId, action).denial
        : undefined;
    },
    usesHistoryBack(screenId, componentId) {
      if (restrictedRevisit) return false;
      const component = compiled.snapshot.screens
        .find((screen) => screen.id === screenId)
        ?.components.find((component) => component.id === componentId);
      return (
        compiled.snapshot.graph.navigation === 'edges' &&
        component?.type === 'back_button' &&
        component.actionIds.length === 0
      );
    },
    initialRatingComponentId: response.existingResponse ? ratings[0].id : undefined,
    initialAcknowledgedAnswers:
      response.existingResponse?.submitted === true
        ? { [ratings[0].id]: { score: response.existingResponse.rating, submitted: true } }
        : {},
    summaryEdit(screenId, values, ratingComponentId) {
      // This configured summary command is internal navigation, never a provider action.
      // Fail closed unless it points to the acknowledged rating's visible owner.
      if (
        restrictedRevisit ||
        (!acknowledgedResponse && base.has_existing_response !== true) ||
        base.response_editing_enabled !== true ||
        !ratingComponentId
      )
        return undefined;
      const owner = ratings.find((component) => component.id === ratingComponentId);
      const ownerScreen = owner
        ? compiled.snapshot.screens.find((candidate) =>
            candidate.components.some((component) => component.id === owner.id)
          )
        : undefined;
      const configuredTarget = compiled.snapshot.configuration.summary.editDestination;
      // The neutral feedback-presentation default follows whichever CSAT/NPS owner
      // produced the acknowledged rating. Explicit authored destinations stay exact.
      const targetScreenId =
        configuredTarget.kind === 'screen'
          ? configuredTarget.screenId
          : configuredTarget.kind === 'terminal' &&
              compiled.snapshot.configuration.summary.editLabel === 'Edit response'
            ? ownerScreen?.id
            : undefined;
      if (!targetScreenId || targetScreenId === screenId) return undefined;
      const currentNode = compiled.snapshot.graph.nodes.find((node) => node.screenId === screenId);
      const targetNode = compiled.snapshot.graph.nodes.find(
        (node) => node.screenId === targetScreenId
      );
      const context = canonicalAnswerContext(base, values, ratingComponentId, compiled);
      if (
        !currentNode ||
        !targetNode ||
        !evaluateFlagGuards(compiled.snapshot, 'stage', currentNode.id, base.resolvedFlags)
          .allowed ||
        !evaluateFlagGuards(compiled.snapshot, 'stage', targetNode.id, base.resolvedFlags).allowed
      )
        return undefined;
      if (
        !owner ||
        canonicalComponentConfig(compiled, owner, base)?.allowResubmit === false ||
        !visiblePresentationComponents(compiled, targetScreenId, context).some(
          (component) => component.id === ratingComponentId
        )
      )
        return undefined;
      const value = values[ratingComponentId];
      if (!value || typeof value !== 'object') return undefined;
      return {
        destination: { kind: 'screen', screenId: targetScreenId, nodeId: targetNode.id },
        values: { ...values, [ratingComponentId]: { ...value, submitted: false } },
        ratingComponentId,
        editResponse: true,
      };
    },
    prepareFeedback(screenId, values, ratingComponentId, skip, acknowledgedAnswers) {
      assertRevisitInteraction();
      return prepareCanonicalFeedback(
        compiled,
        screenId,
        canonicalAnswerContext(base, values, ratingComponentId, compiled),
        values,
        skip,
        acknowledgedAnswers
      );
    },
    verifyFeedbackAcknowledgement(screenId, values, ratingComponentId, savedIds, priorAnswers) {
      if (!policy.persist) return;
      const fields = visiblePresentationComponents(
        compiled,
        screenId,
        canonicalAnswerContext(base, values, ratingComponentId, compiled)
      ).filter((item) => ['tag_selector', 'comment_box'].includes(item.type));
      const ids = fields.map((item) => item.id).filter((id) => values[id] != null);
      if (ratingComponentId) ids.push(ratingComponentId);
      if (
        ids.some(
          (id) =>
            !savedIds.includes(id) &&
            JSON.stringify(priorAnswers[id]) !== JSON.stringify(values[id])
        )
      )
        throw new Error('Feedback persistence did not acknowledge these answers.');
    },
    validateValue(screenId, componentId, values, ratingComponentId, update) {
      assertRevisitInteraction(componentId);
      if (
        !visiblePresentationComponents(
          compiled,
          screenId,
          canonicalAnswerContext(base, values, ratingComponentId, compiled)
        ).some((component) => component.id === componentId)
      )
        throw new Error('This survey component is not available in the current experience.');
      const ratingComponent = ratings.find((component) => component.id === componentId);
      const answerComponent = answerComponents.find((component) => component.id === componentId);
      if (
        update &&
        ratingComponent &&
        (!update.value ||
          typeof update.value !== 'object' ||
          ((update.submission || (update.value as { score?: unknown }).score != null) &&
            !Number.isInteger((update.value as { score?: unknown }).score)))
      )
        throw new Error('A valid rating score is required.');
      if (
        update &&
        answerComponent &&
        values[componentId] &&
        typeof values[componentId] === 'object' &&
        (values[componentId] as { submitted?: boolean }).submitted &&
        (base.response_editing_enabled === false ||
          canonicalComponentConfig(compiled, answerComponent!, base)?.allowResubmit === false)
      )
        throw new Error('This submitted answer cannot be edited.');
      const owner = ratings.some((component) => component.id === componentId)
        ? componentId
        : ratingComponentId;
      enterSurvey(
        compiled,
        canonicalAnswerContext(
          base,
          update ? { ...values, [componentId]: update.value } : values,
          owner,
          compiled
        )
      );
      return owner;
    },
    resolve(screenId, currentValues, previousRatingId, componentId, action) {
      if (restrictedRevisit && !componentId && !action && revisitTip?.node.screenId === screenId)
        return {
          destination: { kind: 'terminal' },
          values: { ...currentValues },
          ratingComponentId: previousRatingId,
        };
      assertRevisitInteraction(componentId);
      const node = compiled.snapshot.graph.nodes.find(
        (candidate) => candidate.screenId === screenId
      );
      if (!node) throw new Error('The active survey screen no longer exists.');
      let values = { ...currentValues };
      let ratingComponentId = previousRatingId;
      if (!componentId) {
        const context = canonicalAnswerContext(base, values, ratingComponentId, compiled);
        return {
          destination: nextSurveyDestination(compiled, node.id, context),
          values,
          ratingComponentId,
        };
      }
      const { component, actionId, paymentSkip, denial } = inspectAction(
        screenId,
        currentValues,
        previousRatingId,
        componentId,
        action
      );
      if (denial) return denial;
      if (restrictedRevisit)
        return {
          destination: { kind: 'terminal' },
          values,
          ratingComponentId,
          action: action ? { ...action, phone: undefined, url: undefined } : undefined,
        };
      if (ratingTypes.has(component.type)) {
        if (!Number.isInteger((values[componentId] as { score?: unknown } | undefined)?.score))
          throw new Error('Choose a rating before submitting your feedback.');
        ratingComponentId = componentId;
      }
      if (
        action &&
        ['review', 'referral'].includes(action.type) &&
        typeof action.value === 'string'
      )
        values[componentId] = action.value;
      const config = canonicalComponentConfig(compiled, component, base);
      if (typeof config?.routeValue === 'string') {
        const key = typeof config.routeValueKey === 'string' ? config.routeValueKey : componentId;
        if (protectedKeys.has(key) || (key !== componentId && componentIds.has(key)))
          throw new Error('This routing value conflicts with protected survey state.');
        values[key] = config.routeValue;
      }
      if (config?.buttonAction === 'edit_response' || action?.type === 'edit_response') {
        if (base.response_editing_enabled === false)
          throw new Error('Response editing is disabled.');
        const owner = ratings.find((item) => item.id === ratingComponentId);
        if (owner && canonicalComponentConfig(compiled, owner, base)?.allowResubmit === false)
          throw new Error('This submitted rating cannot be edited.');
        values = Object.fromEntries(
          Object.entries(values).map(([id, value]) => [
            id,
            id === ratingComponentId && value && typeof value === 'object'
              ? { ...value, submitted: false }
              : value,
          ])
        );
      }
      const editResponse =
        config?.buttonAction === 'edit_response' || action?.type === 'edit_response';
      // A new save permits Edit without turning subsequent routing into a revisit.
      const context = canonicalAnswerContext(
        editResponse && acknowledgedResponse ? { ...base, has_existing_response: true } : base,
        values,
        ratingComponentId,
        compiled
      );
      // Optional tipping may skip through the screen's configured graph continuation.
      // It never executes the owned paid-tip action, and hidden/stale widgets cannot skip.
      if (
        paymentSkip &&
        !actionId &&
        !visiblePresentationComponents(compiled, screenId, context).some(
          (item) => item.id === componentId
        )
      ) {
        throw new Error('This survey component is not available in the current experience.');
      }
      const destination =
        paymentSkip && !actionId
          ? nextSurveyDestination(compiled, node.id, context)
          : presentationComponentDestination(
              compiled,
              screenId,
              componentId,
              context,
              actionId ? { actionId } : undefined
            );
      if (action && !['contact', 'callback', 'referral', 'review'].includes(action.type))
        action = { ...action, phone: undefined, url: undefined };
      const configuredAction = actionId
        ? compiled.snapshot.actions.find((item) => item.id === actionId)
        : undefined;
      if (action && configuredAction?.destinationReference) {
        const reference = configuredAction.destinationReference;
        const phone = response.runtimeContext?.contactPhone?.trim() ?? '';
        if (
          reference !== 'organization.phone' ||
          !['contact', 'callback'].includes(action.type) ||
          !response.runtimeContext?.capabilities?.canContact ||
          !/^\+?[\d\s().-]+$/.test(phone) ||
          !/\d/.test(phone)
        )
          throw new CanonicalActionUnavailableError(
            'This contact or review option is currently unavailable.'
          );
        action = { ...action, url: undefined, phone: phone.replace(/[^\d+]/g, '') };
      }
      if (action && ['review', 'referral'].includes(action.type))
        action = { ...action, phone: undefined };
      if (action?.phone && ['contact', 'callback'].includes(action.type)) {
        const phone = response.runtimeContext?.contactPhone?.trim() ?? '';
        if (
          !response.runtimeContext?.capabilities?.canContact ||
          !/^\+?[\d\s().-]+$/.test(phone) ||
          !/\d/.test(phone)
        )
          throw new CanonicalActionUnavailableError(
            'This contact option is currently unavailable.'
          );
        action = { ...action, phone: phone.replace(/[^\d+]/g, '') };
      }
      if (
        action?.type === 'tip' &&
        (!policy.tip || !compiled.snapshot.configuration.tipping.enabled)
      )
        throw new Error('Tipping is unavailable in this experience.');
      if (policy.preview && action && ['contact', 'callback'].includes(action.type) &&
          !response.runtimeContext?.capabilities?.canContact)
        throw new CanonicalActionUnavailableError('This contact option is currently unavailable.');
      // Trusted host previews emit external-action intent only, without an executable destination.
      if (policy.preview && action && ['review', 'referral', 'contact', 'callback'].includes(action.type))
        action = { ...action, url: undefined, phone: undefined };
      if (
        action &&
        (action.url ||
          action.phone ||
          ['review', 'contact', 'referral', 'callback'].includes(action.type)) &&
        !policy.external &&
        !(policy.preview && ['review', 'referral', 'contact', 'callback'].includes(action.type))
      )
        throw new Error('Test mode does not open external destinations.');
      return {
        destination,
        values,
        ratingComponentId,
        action,
        editResponse: config?.buttonAction === 'edit_response' || action?.type === 'edit_response',
      };
    },
  };
}
